import type { LayoutConfig } from '@/utils/dealCalculations'
import {
  DEFAULT_BONUS_Y,
  DEFAULT_DESCRIPTION_Y,
  DEFAULT_LOYALTY_Y,
  DEFAULT_NEGOTIATION_Y,
  DEFAULT_PAYMENT_Y,
  DEFAULT_PRICE_Y,
  DEFAULT_PROJECT_TYPE_Y,
  DEFAULT_SUPPORT_HOURS_Y,
  DEFAULT_TITLE_Y,
} from '@/utils/dealCalculations'

/** Blocos fixos do slide de investimento (numerados para templates e suporte). */
export type FixedLayoutBlockId =
  | 'leftBlockWidth'
  | 'title'
  | 'projectType'
  | 'description'
  | 'supportHours'
  | 'bonus'
  | 'price'
  | 'payment'
  | 'loyalty'
  | 'negotiation'
  | 'planTitle'
  | 'serviceCol1X'
  | 'serviceCol2X'
  | 'serviceStartY'

export interface FixedLayoutBlockDef {
  number: number
  id: FixedLayoutBlockId
  label: string
  hint: string
  zone: 'estrutura' | 'bloco-verde' | 'area-branca' | 'grade-servicos'
  layoutKey?: keyof LayoutConfig
  axis: 'width' | 'y' | 'x'
  min: number
  max: number
  step: number
  defaultValue: number
}

export const FIXED_SLIDE_LAYOUT_BLOCKS: FixedLayoutBlockDef[] = [
  {
    number: 1,
    id: 'leftBlockWidth',
    label: 'Divisor bloco verde / branco',
    hint: 'Largura da faixa verde à esquerda',
    zone: 'estrutura',
    layoutKey: 'leftBlockWidth',
    axis: 'width',
    min: 20,
    max: 50,
    step: 0.5,
    defaultValue: 35,
  },
  {
    number: 2,
    id: 'title',
    label: 'Título "Investimentos."',
    hint: 'Topo do bloco verde',
    zone: 'bloco-verde',
    layoutKey: 'titleY',
    axis: 'y',
    min: 1,
    max: 25,
    step: 0.5,
    defaultValue: DEFAULT_TITLE_Y,
  },
  {
    number: 3,
    id: 'projectType',
    label: 'Tipo do projeto',
    hint: 'Rótulo do tipo, só se o plano tiver',
    zone: 'bloco-verde',
    layoutKey: 'projectTypeY',
    axis: 'y',
    min: 1,
    max: 90,
    step: 0.5,
    defaultValue: DEFAULT_PROJECT_TYPE_Y,
  },
  {
    number: 4,
    id: 'description',
    label: 'Descrição do plano',
    hint: 'Texto cinza no bloco verde',
    zone: 'bloco-verde',
    layoutKey: 'descriptionY',
    axis: 'y',
    min: 5,
    max: 90,
    step: 0.5,
    defaultValue: DEFAULT_DESCRIPTION_Y,
  },
  {
    number: 5,
    id: 'supportHours',
    label: 'Horas de suporte',
    hint: 'Informativo abaixo da descrição (ex.: 15h/mês)',
    zone: 'bloco-verde',
    layoutKey: 'supportHoursY',
    axis: 'y',
    min: 5,
    max: 90,
    step: 0.5,
    defaultValue: DEFAULT_SUPPORT_HOURS_Y,
  },
  {
    number: 6,
    id: 'bonus',
    label: 'Destaque bônus',
    hint: 'Caixa verde de bônus',
    zone: 'bloco-verde',
    layoutKey: 'bonusY',
    axis: 'y',
    min: 5,
    max: 90,
    step: 0.5,
    defaultValue: DEFAULT_BONUS_Y,
  },
  {
    number: 7,
    id: 'price',
    label: 'Valor principal',
    hint: 'Preço em destaque (R$)',
    zone: 'bloco-verde',
    layoutKey: 'priceY',
    axis: 'y',
    min: 5,
    max: 90,
    step: 0.5,
    defaultValue: DEFAULT_PRICE_Y,
  },
  {
    number: 8,
    id: 'payment',
    label: 'Detalhes de pagamento',
    hint: 'Parcelas / entrada',
    zone: 'bloco-verde',
    layoutKey: 'paymentY',
    axis: 'y',
    min: 5,
    max: 90,
    step: 0.5,
    defaultValue: DEFAULT_PAYMENT_Y,
  },
  {
    number: 9,
    id: 'loyalty',
    label: 'Fidelidade / duração',
    hint: 'Ex: Fidelidade 12 meses',
    zone: 'bloco-verde',
    layoutKey: 'loyaltyY',
    axis: 'y',
    min: 5,
    max: 90,
    step: 0.5,
    defaultValue: DEFAULT_LOYALTY_Y,
  },
  {
    number: 10,
    id: 'negotiation',
    label: 'Validade da negociação',
    hint: 'Ex: Negociação válida até 15/06',
    zone: 'bloco-verde',
    layoutKey: 'negotiationY',
    axis: 'y',
    min: 5,
    max: 95,
    step: 0.5,
    defaultValue: DEFAULT_NEGOTIATION_Y,
  },
  {
    number: 11,
    id: 'planTitle',
    label: 'Nome do plano',
    hint: 'Título no topo da área branca',
    zone: 'area-branca',
    layoutKey: 'planTitleY',
    axis: 'y',
    min: 1,
    max: 40,
    step: 0.5,
    defaultValue: 2.7,
  },
  {
    number: 12,
    id: 'serviceCol1X',
    label: 'Coluna 1 — posição X',
    hint: 'Início da 1ª coluna de entregáveis',
    zone: 'grade-servicos',
    layoutKey: 'serviceBlockX',
    axis: 'x',
    min: 25,
    max: 75,
    step: 0.5,
    defaultValue: 38,
  },
  {
    number: 13,
    id: 'serviceCol2X',
    label: 'Coluna 2 — posição X',
    hint: 'Início da 2ª coluna de entregáveis',
    zone: 'grade-servicos',
    layoutKey: 'serviceBlockGap',
    axis: 'x',
    min: 35,
    max: 95,
    step: 0.5,
    defaultValue: 68,
  },
  {
    number: 14,
    id: 'serviceStartY',
    label: 'Grade — posição Y inicial',
    hint: 'Altura onde começam os blocos de categoria',
    zone: 'grade-servicos',
    layoutKey: 'serviceBlockStartY',
    axis: 'y',
    min: 5,
    max: 80,
    step: 0.5,
    defaultValue: 10,
  },
]

export const ZONE_LABELS: Record<FixedLayoutBlockDef['zone'], string> = {
  estrutura: 'Estrutura do slide',
  'bloco-verde': 'Bloco verde (esquerda)',
  'area-branca': 'Área branca (direita)',
  'grade-servicos': 'Grade de entregáveis (padrão)',
}

export const CATEGORY_BLOCK_NUMBER_START =
  FIXED_SLIDE_LAYOUT_BLOCKS[FIXED_SLIDE_LAYOUT_BLOCKS.length - 1].number + 1

export const FIXED_BLOCK_BY_ID = Object.fromEntries(
  FIXED_SLIDE_LAYOUT_BLOCKS.map((b) => [b.id, b]),
) as Record<FixedLayoutBlockId, FixedLayoutBlockDef>
